import { IonContent, IonPage } from '@ionic/react';
import { useCallback, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import DefaultBehaviorToast from './DefaultBehaviorToast';
import { SettingRow, SettingsToggle } from './SettingRow';
import SettingsHeader from './SettingsHeader';
import { findNotificationGroup } from './notificationOptions';
import { isSameSettingValue } from './settingsOptions';
import { useUserSettings } from './useUserSettings';

// One Notification Settings sub page (News and Updates, All/Favorite/Playlist Slots).
// "Allow notifications" switches the group off without losing its choices below.
const NotificationGroupSettings: React.FC = () => {
  const { group: slug } = useParams<{ group: string }>();
  const { settings, defaults, updateSetting, resetSettings, visibility } = useUserSettings();
  const [showDefaultToast, setShowDefaultToast] = useState(false);
  const closeDefaultToast = useCallback(() => setShowDefaultToast(false), []);

  const group = findNotificationGroup(slug);
  if (!visibility.settings.notifications.editNotifications) {
    return <Redirect to="/settings" />;
  }
  if (!group || !visibility.notificationSettings.groups[group.visibilityKey]) {
    return <Redirect to="/settings/notifications" />;
  }
  // News and Updates has its own rows; the three slot pages share slotNotifications.
  const shown: Record<string, boolean> =
    group.key === 'newsAndUpdates' ? visibility.newsAndUpdates : visibility.slotNotifications;
  const visibleItems = group.items.filter(({ field }) => shown[field] !== false);

  const groupValue = settings?.[group.key];
  const value: Record<string, boolean> | undefined = groupValue;
  const isLocked = !value?.enabled;

  const setField = (field: string, checked: boolean) => {
    if (groupValue) {
      updateSetting(group.key, { ...groupValue, [field]: checked });
    }
  };

  const handleReset = () => {
    if (settings && defaults && isSameSettingValue(settings[group.key], defaults[group.key])) {
      setShowDefaultToast(true);
      return;
    }
    resetSettings([group.key]);
  };

  return (
    <IonPage className="settings-page">
      <SettingsHeader />
      <IonContent className="settings-content">
        <div className="settings-options__intro">
          <h2 className="settings-group__title">{group.title}</h2>
          <p className="settings-options__description">{group.description}</p>
        </div>

        <div className="settings-card">
          <SettingRow label="Allow notifications">
            <SettingsToggle
              checked={!!value?.enabled}
              label="Allow notifications"
              disabled={!value}
              onChange={(checked) => setField('enabled', checked)}
            />
          </SettingRow>
        </div>

        {visibleItems.length > 0 && (
          <div className={`settings-card${isLocked ? ' settings-card--locked' : ''}`}>
            {visibleItems.map(({ field, label }) => (
              <SettingRow key={field} label={label}>
                <SettingsToggle
                  checked={!!value?.[field]}
                  label={label}
                  disabled={isLocked}
                  onChange={(checked) => setField(field, checked)}
                />
              </SettingRow>
            ))}
          </div>
        )}

        {shown.resetToDefault && (
          <button
            className="settings-card settings-action"
            disabled={!settings}
            onClick={handleReset}
          >
            <span className="settings-action__label">Reset to Default</span>
          </button>
        )}
      </IonContent>

      {showDefaultToast && <DefaultBehaviorToast onClose={closeDefaultToast} />}
    </IonPage>
  );
};

export default NotificationGroupSettings;
